import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useLoader } from "src/hooks/useLoader";
import { z } from "src/lib";
import { ContextOperacionNuevo, ContextOperacionNuevoType } from "../../NuevoSistema";
import { HttpWRM, OperacionWRMFields } from "./api";
import { SeccionWRMDatosOperacion } from "./components";
import { SeccionWRMDetalleNuevo } from "./components/SeccionWRMDetalleNuevo";
import { useSubmitOperacion } from "../../hooks/useSubmitOperacion";

export const DetalleProvisionalFields = {
  IdProvisional: "idProvisional",
  CodOperacionLimiteTipo: 'codOperacionLimiteTipo',
  Excepcion: 'excepcion',
  ValorNominal: 'valorNominal',
  CashValueUSD: 'cashValueUSD',
  Instrumento: 'instrumento'
} as const

export const detalleProvisional = z
  .object({
    [DetalleProvisionalFields.IdProvisional]: z.string(),
    [DetalleProvisionalFields.CodOperacionLimiteTipo]: z.number(),
    [DetalleProvisionalFields.Excepcion]: z.string(),
    [DetalleProvisionalFields.ValorNominal]: z.coerce.number(),
    [DetalleProvisionalFields.CashValueUSD]: z.coerce.number(),
    [DetalleProvisionalFields.Instrumento]: z.string()
  })

export type DetalleProvisional = z.infer<typeof detalleProvisional>

const schemaNuevaOperacion = z.object({
  [OperacionWRMFields.CodSectorComercial]: z.number(),
  [OperacionWRMFields.Observaciones]: z.string().optional(),
  [OperacionWRMFields.FechaEmision]: z.date(),
  [OperacionWRMFields.FechaVencimiento]: z.date()
}).refine(
  schema => (

    // No uso computed key porque: https://github.com/microsoft/TypeScript/issues/10530
    schema.fechaVencimiento ? (schema.fechaVencimiento.getTime() >= schema.fechaEmision.getTime()) : true
  ),
  {
    message: 'La fecha de vencimiento debe ser mayor a la fecha de emisión.',
    path: [OperacionWRMFields.FechaVencimiento]
  }
)

type SchemaNuevaOperacion = z.infer<typeof schemaNuevaOperacion>

type NuevoSistemaWRMercadoProps = {
  codSistemaPrincipal: number
}

export const NuevoSistemaWRMercado = (props: NuevoSistemaWRMercadoProps) => {
  const { submitOperacion } = useSubmitOperacion();

  const [detallesProvisionales, setDetallesProvisionales] = useState<DetalleProvisional[]>([])

  const formMethods = useForm<SchemaNuevaOperacion>({
    resolver: zodResolver(schemaNuevaOperacion),
    defaultValues: {
      [OperacionWRMFields.FechaEmision]: new Date()
    }
  })

  const alCrearOperacionWRM = useCallback((data: SchemaNuevaOperacion, selected: number) => {
    if (!detallesProvisionales.length) {
      toast.error('Debe cargar por lo menos un detalle para enviar la operación.');
      
      return;
    }

    const payload = {
      ...data,
      [OperacionWRMFields.Detalles]: detallesProvisionales,
      [OperacionWRMFields.CodPais]: props.codSistemaPrincipal,
      [OperacionWRMFields.BitEnvio]: selected !== 1
    };

    submitOperacion(payload, {
      apiCall: HttpWRM.post,
      successMessage: 'Guardado correctamente.',
      errorMessage: 'Error al guardar.'
    });
  }, [detallesProvisionales, props.codSistemaPrincipal, submitOperacion]);

  const { setSendPromise, codSistema } = useContext<ContextOperacionNuevoType>(ContextOperacionNuevo)

  useEffect(() => {
    const onSend = () => formMethods.handleSubmit(data => alCrearOperacionWRM(data, 2))
    setSendPromise(onSend)
  }, [formMethods, alCrearOperacionWRM])

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <SeccionWRMDatosOperacion
            formMethods={formMethods}
            codSistemaPrincipal={props.codSistemaPrincipal}
            codSistema={codSistema}
            nuevo
          />
        </Grid>
        <Grid item xs={12}>
          <SeccionWRMDetalleNuevo
            idPais={props.codSistemaPrincipal}
            detallesProvisionales={detallesProvisionales}
            setDetallesProvisionales={setDetallesProvisionales}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
